import {
  loginIcon,
  ExcalLogo,
  eyeIcon,
  share,
  usersIcon,
} from "@excalidraw/excalidraw/components/icons";
import { useI18n } from "@excalidraw/excalidraw/i18n";
import { MainMenu } from "@excalidraw/excalidraw/index";
import React from "react";

import { DEFAULT_VERSION, getVersion, isDevEnv } from "@excalidraw/common";

import type { Theme } from "@excalidraw/element/types";
import type { ExcalidrawImperativeAPI } from "@excalidraw/excalidraw/types";

import { LanguageList } from "../app-language/LanguageList";
import { isDrawolaPlusSignedUser } from "../app_constants";
import { DRAWOLA_FEATURES } from "../drawola-features";

// DRAWOLA:CUSTOM-START SHOW_MINIMAP
import { useDrawolaT } from "../storage/i18n";

import { FileListSidebar } from "../storage/ui/FileListSidebar";

import { CloudSubmenu } from "../cloud/ui/CloudSubmenu";

import { MinimapPreferenceItem } from "./MinimapPreferenceItem";
import { ShapeLibraryPreferenceItem } from "./ShapeLibraryPreferenceItem";
// DRAWOLA:CUSTOM-END

// DRAWOLA:CUSTOM-START ENABLE_MULTI_FILE_STORE

import { saveDebugState } from "./DebugCanvas";

import type { useMultiFileStore } from "../storage/useMultiFileStore";
// DRAWOLA:CUSTOM-END

// DRAWOLA:CUSTOM-START SHOW_CLOUD_STORAGE
// DRAWOLA:CUSTOM-END

// DRAWOLA:CUSTOM-START SHOW_CLOUD_STORAGE sync
import type { CloudSyncApi } from "../cloud/sync";
// DRAWOLA:CUSTOM-END

// DRAWOLA:CUSTOM-START ENABLE_MULTI_FILE_STORE
// Simple folder SVG icon for the drawOla Files menu entry. Kept inline so we
// do not depend on an upstream icon name that may change.
const FOLDER_ICON = (
  <svg
    aria-hidden="true"
    focusable="false"
    role="img"
    viewBox="0 0 20 20"
    width="16"
    height="16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M2.5 5.5A1.5 1.5 0 0 1 4 4h3.086a1.5 1.5 0 0 1 1.06.44L9.5 5.79h6.5A1.5 1.5 0 0 1 17.5 7.29V14A1.5 1.5 0 0 1 16 15.5H4A1.5 1.5 0 0 1 2.5 14V5.5Z" />
  </svg>
);
// DRAWOLA:CUSTOM-END

// DRAWOLA:CUSTOM-START SHOW_CLOUD_STORAGE
const CLOUD_ICON = (
  <svg
    aria-hidden="true"
    focusable="false"
    role="img"
    viewBox="0 0 20 20"
    width="16"
    height="16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M6 15a3.5 3.5 0 0 1-.5-6.97 4.5 4.5 0 0 1 8.95.6A3 3 0 0 1 15 15H6Z" />
  </svg>
);
// DRAWOLA:CUSTOM-END

// DRAWOLA:CUSTOM-START SHOW_SHARE_LINK
// "Shared links" glyph for the manage-links menu entry. Inline for the same
// reason as the icons above: no dependency on an upstream icon name.
const SHARES_ICON = (
  <svg
    aria-hidden="true"
    focusable="false"
    role="img"
    viewBox="0 0 20 20"
    width="16"
    height="16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M9 11.5 11.5 9" />
    <path d="M7.5 12.5 6 14a2.2 2.2 0 0 1-3-3L5 9a2.2 2.2 0 0 1 3 0" />
    <path d="M12.5 7.5 14 6a2.2 2.2 0 0 1 3 3L15 11a2.2 2.2 0 0 1-3 0" />
  </svg>
);
// DRAWOLA:CUSTOM-END

// DRAWOLA:CUSTOM-START version footer
const getDrawolaVersion = (): { short: string; full: string } => {
  const raw = getVersion();
  if (raw === DEFAULT_VERSION) {
    return { short: "dev", full: "dev" };
  }
  const lastDash = raw.lastIndexOf("-");
  if (lastDash === -1) {
    return { short: raw, full: raw };
  }
  return { short: raw.slice(lastDash + 1), full: raw };
};

const DRAWOLA_VERSION = getDrawolaVersion();
// DRAWOLA:CUSTOM-END

export const AppMainMenu: React.FC<{
  onCollabDialogOpen: () => any;
  isCollaborating: boolean;
  isCollabEnabled: boolean;
  // DRAWOLA:CUSTOM-START SHOW_LIVE_COLLAB availability
  /** `null` shows the entry normally; any other value disables it and explains why. */
  collabDisabledReason: string | null;
  // DRAWOLA:CUSTOM-END
  theme: Theme | "system";
  refresh: () => void;
  // DRAWOLA:CUSTOM-START ENABLE_MULTI_FILE_STORE
  multiFile?: ReturnType<typeof useMultiFileStore>;
  // DRAWOLA:CUSTOM-END
  // DRAWOLA:CUSTOM-START SHOW_CLOUD_STORAGE
  excalidrawAPI?: ExcalidrawImperativeAPI | null;
  // DRAWOLA:CUSTOM-END
  // DRAWOLA:CUSTOM-START SHOW_CLOUD_STORAGE sync
  /** Cloud sync state and actions; absent when cloud storage is off. */
  cloudSync?: CloudSyncApi;
  // DRAWOLA:CUSTOM-END
  // DRAWOLA:CUSTOM-START SHOW_SHARE_LINK
  /** Upload the open scene and show its share link. Undefined hides the entry. */
  onShareLink?: () => void;
  /** Open the share-management list for the open draw. Undefined hides the entry. */
  onManageShares?: () => void;
  // DRAWOLA:CUSTOM-END
}> = React.memo((props) => {
  const { t } = useI18n();
  // DRAWOLA:CUSTOM-START ENABLE_MULTI_FILE_STORE
  const drawolaT = useDrawolaT();
  // DRAWOLA:CUSTOM-END
  return (
    <MainMenu>
      {/* DRAWOLA:CUSTOM-START ENABLE_MULTI_FILE_STORE */}
      {DRAWOLA_FEATURES.ENABLE_MULTI_FILE_STORE && props.multiFile && (
        <MainMenu.Sub>
          <MainMenu.Sub.Trigger icon={FOLDER_ICON}>
            {drawolaT("files.menuEntry")}
          </MainMenu.Sub.Trigger>
          <MainMenu.Sub.Content className="drawOla-files-submenu">
            <FileListSidebar
              activeDocId={props.multiFile.activeDocId}
              refreshToken={props.multiFile.refreshToken}
              onSwitch={(id) => props.multiFile?.switchTo(id)}
              onCreate={(id) => props.multiFile?.onCreated(id)}
              onActiveDeleted={() => props.multiFile?.onActiveDeleted()}
            />
          </MainMenu.Sub.Content>
        </MainMenu.Sub>
      )}
      {/* DRAWOLA:CUSTOM-END */}
      {/* DRAWOLA:CUSTOM-START SHOW_CLOUD_STORAGE */}
      {DRAWOLA_FEATURES.SHOW_CLOUD_STORAGE &&
        props.excalidrawAPI &&
        props.multiFile &&
        props.cloudSync && (
          <MainMenu.Sub>
            <MainMenu.Sub.Trigger icon={CLOUD_ICON}>Cloud</MainMenu.Sub.Trigger>
            <MainMenu.Sub.Content className="drawOla-cloud-submenu">
              <CloudSubmenu
                excalidrawAPI={props.excalidrawAPI}
                multiFile={props.multiFile}
                cloudSync={props.cloudSync}
              />
            </MainMenu.Sub.Content>
          </MainMenu.Sub>
        )}
      {/* DRAWOLA:CUSTOM-END */}
      <MainMenu.DefaultItems.LoadScene />
      <MainMenu.DefaultItems.SaveToActiveFile />
      <MainMenu.DefaultItems.Export />
      <MainMenu.DefaultItems.SaveAsImage />
      {/* DRAWOLA:CUSTOM-START SHOW_SHARE_LINK */}
      {/* Upstream's own string, so every shipped language already has it. */}
      {DRAWOLA_FEATURES.SHOW_SHARE_LINK && props.onShareLink && (
        <MainMenu.Item icon={share} onSelect={props.onShareLink}>
          {t("buttons.getShareableLink")}
        </MainMenu.Item>
      )}
      {DRAWOLA_FEATURES.SHOW_SHARE_LINK && props.onManageShares && (
        <MainMenu.Item icon={SHARES_ICON} onSelect={props.onManageShares}>
          {drawolaT("studio.menu.manageShares")}
        </MainMenu.Item>
      )}
      {/* DRAWOLA:CUSTOM-END */}
      {/* DRAWOLA:CUSTOM-START SHOW_LIVE_COLLAB */}
      {DRAWOLA_FEATURES.SHOW_LIVE_COLLAB &&
        props.isCollabEnabled &&
        (props.collabDisabledReason !== null ? (
          <MainMenu.Item
            icon={usersIcon}
            disabled
            title={props.collabDisabledReason}
            aria-label={props.collabDisabledReason}
            onSelect={() => {}}
          >
            {t("labels.liveCollaboration")}
          </MainMenu.Item>
        ) : (
          <MainMenu.DefaultItems.LiveCollaborationTrigger
            isCollaborating={props.isCollaborating}
            onSelect={() => props.onCollabDialogOpen()}
          />
        ))}
      {/* DRAWOLA:CUSTOM-END */}
      <MainMenu.DefaultItems.CommandPalette className="highlighted" />
      {/* DRAWOLA:CUSTOM-START SHOW_FIND_ON_CANVAS */}
      {DRAWOLA_FEATURES.SHOW_FIND_ON_CANVAS && (
        <MainMenu.DefaultItems.SearchMenu />
      )}
      {/* DRAWOLA:CUSTOM-END */}
      <MainMenu.DefaultItems.Help />
      <MainMenu.DefaultItems.ClearCanvas />
      <MainMenu.Separator />
      {/* DRAWOLA:CUSTOM-START SHOW_DRAWOLA_PLUS */}
      {DRAWOLA_FEATURES.SHOW_DRAWOLA_PLUS && (
        <MainMenu.ItemLink
          icon={ExcalLogo}
          href={`${
            import.meta.env.VITE_APP_PLUS_LP
          }/plus?utm_source=drawola&utm_medium=app&utm_content=hamburger`}
          className=""
        >
          drawOla+
        </MainMenu.ItemLink>
      )}
      {/* DRAWOLA:CUSTOM-END */}
      {/* DRAWOLA:CUSTOM-START SHOW_SOCIALS (GitHub + Follow us + Discord) */}
      {DRAWOLA_FEATURES.SHOW_SOCIALS && <MainMenu.DefaultItems.Socials />}
      {/* DRAWOLA:CUSTOM-END */}
      {/* DRAWOLA:CUSTOM-START SHOW_SIGN_UP */}
      {DRAWOLA_FEATURES.SHOW_SIGN_UP && (
        <MainMenu.ItemLink
          icon={loginIcon}
          href={`${import.meta.env.VITE_APP_PLUS_APP}${
            isDrawolaPlusSignedUser ? "" : "/sign-up"
          }?utm_source=signin&utm_medium=app&utm_content=hamburger`}
          className="highlighted"
        >
          {isDrawolaPlusSignedUser ? t("labels.signIn") : t("labels.signUp")}
        </MainMenu.ItemLink>
      )}
      {/* DRAWOLA:CUSTOM-END */}
      {/* DRAWOLA:CUSTOM-START SHOW_VISUAL_DEBUG */}
      {DRAWOLA_FEATURES.SHOW_VISUAL_DEBUG && isDevEnv() && (
        <MainMenu.Item
          icon={eyeIcon}
          onSelect={() => {
            if (window.visualDebug) {
              delete window.visualDebug;
              saveDebugState({ enabled: false });
            } else {
              window.visualDebug = { data: [] };
              saveDebugState({ enabled: true });
            }
            props?.refresh();
          }}
        >
          Visual Debug
        </MainMenu.Item>
      )}
      {/* DRAWOLA:CUSTOM-END */}
      <MainMenu.Separator />
      {/* DRAWOLA:CUSTOM-START SHOW_MINIMAP
          We reproduce upstream's default Preferences items here (instead of
          relying on the implicit list) so the fork-only Minimap entry can sit
          directly after "Select on" rather than at the bottom. If upstream
          adds new default items, mirror them here. */}
      <MainMenu.DefaultItems.Preferences>
        <MainMenu.DefaultItems.Preferences.BoxSelectionMode />
        {DRAWOLA_FEATURES.SHOW_MINIMAP && <MinimapPreferenceItem />}
        <ShapeLibraryPreferenceItem />
        <MainMenu.DefaultItems.Preferences.ToggleToolLock />
        <MainMenu.DefaultItems.Preferences.ToggleSnapMode />
        <MainMenu.DefaultItems.Preferences.ToggleGridMode />
        <MainMenu.DefaultItems.Preferences.ToggleZenMode />
        <MainMenu.DefaultItems.Preferences.ToggleViewMode />
        <MainMenu.DefaultItems.Preferences.ToggleElementProperties />
        <MainMenu.DefaultItems.Preferences.ToggleArrowBinding />
        <MainMenu.DefaultItems.Preferences.ToggleMidpointSnapping />
      </MainMenu.DefaultItems.Preferences>
      {/* DRAWOLA:CUSTOM-END */}
      <MainMenu.DefaultItems.ToggleTheme allowSystemTheme theme={props.theme} />
      <MainMenu.ItemCustom>
        <LanguageList style={{ width: "100%" }} />
      </MainMenu.ItemCustom>
      <MainMenu.DefaultItems.ChangeCanvasBackground />
      {/* DRAWOLA:CUSTOM-START version footer */}
      <MainMenu.Separator />
      <MainMenu.ItemCustom>
        <div
          title={DRAWOLA_VERSION.full}
          style={{
            width: "100%",
            padding: "0.25rem 0.5rem",
            fontSize: "0.7rem",
            opacity: 0.5,
            textAlign: "center",
            fontFamily:
              "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
            userSelect: "text",
          }}
        >
          {DRAWOLA_VERSION.short}
        </div>
      </MainMenu.ItemCustom>
      {/* DRAWOLA:CUSTOM-END */}
    </MainMenu>
  );
});
